import { Button, Checkbox, message, Modal, Radio, Select, Space } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { serverFetch } from "../../server";
import dayjs from "dayjs";
import Client from "../clients/client";
import "./records.css";

/** Оформление визита по записи */
const RecordVisit = ({ item, onClose }) => {
  const needsAbonement = useMemo(() => item.forPayment === false, [item.forPayment]);

  const allClients = useMemo(
    () => item.cnts.filter((c) => c.objectVid === 1),
    [item.cnts]
  );

  /** За плату: как раньше — несколько клиентов через чекбоксы */
  const [paidSelectedIds, setPaidSelectedIds] = useState([]);
  /** По абонементу: один клиент — Radio */
  const [selectedClientCntId, setSelectedClientCntId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [abon, setAbon] = useState();
  const [abons, setAbons] = useState([]);
  /** { id: objectId клиента, name: objectName } при открытой форме клиента */
  const [editClient, setEditClient] = useState(null);

  const unusedClientsSignature = useMemo(
    () =>
      item.cnts
        .filter((c) => c.objectVid === 1 && !c.used)
        .map((c) => c.id)
        .join(","),
    [item.cnts]
  );

  useEffect(() => {
    if (!needsAbonement) {
      setPaidSelectedIds(
        item.cnts.filter((i) => !i.used && i.objectVid === 1).map((i) => i.id)
      );
      return;
    }
    const unused = item.cnts.filter((c) => c.objectVid === 1 && !c.used);
    setSelectedClientCntId(unused.length ? unused[0].id : null);
  }, [item.id, unusedClientsSignature, needsAbonement, item.cnts]);

  const selectedRow = useMemo(
    () => allClients.find((c) => c.id === selectedClientCntId),
    [allClients, selectedClientCntId]
  );
  const selectedClientObjectId = selectedRow?.objectId;

  const loadAbons = useCallback(() => {
    if (!needsAbonement || selectedClientObjectId == null) {
      setAbons([]);
      setAbon(undefined);
      return;
    }
    serverFetch(`sale/ListByClient/${selectedClientObjectId}?vids=2`)
      .then((data) => {
        const newAbons = data
          .filter((i) => i.status === 2 && !i.deleted)
          .map((i) => ({ label: `[${i.id}] ${i.productName}`, value: i.id }));
        setAbons(newAbons);
        setAbon((prev) => {
          if (newAbons.length === 0) return undefined;
          if (prev != null && newAbons.some((a) => a.value === prev)) return prev;
          return newAbons[0].value;
        });
      })
      .catch((e) => message.error(e.userMessage));
  }, [needsAbonement, selectedClientObjectId]);

  useEffect(() => {
    loadAbons();
  }, [loadAbons]);

  const handleCloseClientForm = useCallback(() => {
    setEditClient(null);
    if (needsAbonement) {
      loadAbons();
    }
  }, [needsAbonement, loadAbons]);

  const togglePaidClient = (id, checked) => {
    setPaidSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((x) => x !== id)
    );
  };

  const divClientsPaid = allClients.map((c) => (
    <div className="record-visit-item" key={c.id}>
      <Space>
        <Checkbox
          checked={paidSelectedIds.includes(c.id)}
          disabled={c.used}
          onChange={(e) => togglePaidClient(c.id, e.target.checked)}
        >
          {c.objectName}
        </Checkbox>
        <Button onClick={() => setEditClient({ id: c.objectId, name: c.objectName })}>
          Изменить
        </Button>
      </Space>
    </div>
  ));

  const divClientsAbon = (
    <Radio.Group
      className="record-visit-client-radios"
      value={selectedClientCntId ?? undefined}
      onChange={(e) => setSelectedClientCntId(e.target.value)}
    >
      {allClients.map((c) => (
        <div className="record-visit-item" key={c.id}>
          <Space>
            <Radio value={c.id} disabled={c.used}>
              {c.objectName}
            </Radio>
            <Button onClick={() => setEditClient({ id: c.objectId, name: c.objectName })}>
              Изменить
            </Button>
          </Space>
        </div>
      ))}
    </Radio.Group>
  );

  const divTovs = item.cnts
    .filter((i) => i.objectVid === 2)
    .map((i) => (
      <div className="record-visit-item" key={i.id}>
        {i.objectName}
      </div>
    ));

  const canOpenVisit = needsAbonement
    ? selectedClientCntId != null && abon != null && abons.length > 0
    : paidSelectedIds.length > 0;

  const onSave = () => {
    if (!canOpenVisit) return;
    setLoading(true);
    serverFetch("visit/AddByRecords", {
      method: "POST",
      bodyData: needsAbonement
        ? {
            recordCnts: [selectedClientCntId],
            saleId: abon,
          }
        : {
            recordCnts: paidSelectedIds,
            saleId: null,
          },
    })
      .then(() => onClose(true))
      .catch((e) => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  const timeWarning = useMemo(() => {
    if (!item?.start || !item?.finish) return null;
    const now = dayjs();
    if (now.isBefore(item.start)) return "Время записи еще не наступило";
    if (now.isAfter(item.finish)) return "Время записи уже прошло";
    return null;
  }, [item]);

  return (
    <>
    <Modal open onCancel={() => onClose()} footer={null} title="Визит по записи" loading={loading}>
      <div className="record-visit-title">{needsAbonement ? "Клиент" : "Клиенты"}</div>
      {needsAbonement ? divClientsAbon : divClientsPaid}
      {needsAbonement && (
        <>
          <div className="record-visit-title">Абонемент</div>
          {!selectedClientCntId ? (
            <div className="record-visit-item record-visit-hint">Выберите клиента</div>
          ) : (
            <div className="record-visit-item">
              <Select className="record-visit-select" options={abons} value={abon} onChange={setAbon} />
            </div>
          )}
        </>
      )}
      <div className="record-visit-title">Услуги</div>
      {divTovs}
      {timeWarning && <div className="record-visit-time-warning">{timeWarning}</div>}
      <div className="record-visit-footer">
        <Button disabled={!canOpenVisit} className="dolg-button" onClick={onSave}>
          Открыть визит
        </Button>
      </div>
    </Modal>
    {editClient != null && (
      <div className="record-visit-client-modal-front">
        <Client
          id={editClient.id}
          name={editClient.name}
          onClose={handleCloseClientForm}
        />
      </div>
    )}
    </>
  );
};

export default RecordVisit;
